/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import Image from 'next/image'
import { useLocale } from 'next-intl'
import React from 'react'
import { Link, usePathname } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'
import { cn } from '@/utils/css'
import { convertLocaleToLanguage } from '@/utils/locale-to-language'

/**
 * Language switch that inserts into the desktop header. Enables the user to switch to another language.
 */
export const HeaderLanguageSwitch: React.FC = () => {
  const pathname = usePathname()
  const locale = useLocale()

  return (
    <div className='relative group h-full flex items-center'>
      <button className='flex items-center py-1 gap-1 focus:outline-none'>
        {locale === 'en' && (
          <Image
            src={'/images/icons/en-locale.svg'}
            width={28.5}
            height={15}
            alt={`${convertLocaleToLanguage(locale)}`}
          />
        )}
        {locale === 'es' && (
          <Image
            src={'/images/icons/es-locale.svg'}
            width={28.5}
            height={15}
            alt={`${convertLocaleToLanguage(locale)}`}
          />
        )}
        <span className='text-primary-500 text-sm font-medium'>{convertLocaleToLanguage(locale) || 'Language'}</span>
      </button>
      <div className='absolute right-0 top-full mt-0 w-32 bg-white rounded-md shadow-lg py-1 z-10 hidden group-hover:block'>
        {routing.locales.map((lang) => (
          <Link
            key={lang}
            href={pathname}
            locale={lang}
            className={cn(
              'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100',
              locale === lang && 'font-bold text-primary-600',
            )}
          >
            {convertLocaleToLanguage(lang) || lang.toUpperCase()}
          </Link>
        ))}
      </div>
    </div>
  )
}
